/**
 * ConferenceModal Component
 * 
 * Comprehensive modal for creating and editing conferences with all 12 required fields.
 * Includes support for HTML editing, file uploads, and photo uploads.
 * 
 * @component
 */
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Card, Badge } from 'react-bootstrap';
import WysiwygEditor from '../Common/WysiwygEditor';

const ConferenceModal = ({
    show,
    onHide,
    editingConference,
    conferenceFormData,
    onFormChange,
    onSubmit,
    onReset,
    conferenceStatuses,
    getFileIcon,
    onDeleteFile,
    onDeletePhoto,
    onSetBookletFile
}) => {
    // Modal state
    const [conferenceFormTab, setConferenceFormTab] = useState('form');
    const [activeFormSection, setActiveFormSection] = useState('basic');
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Form sections for better organization
    const formSections = [
        { id: 'basic', label: 'Basic Info', icon: 'bi-info-circle' },
        { id: 'content', label: 'Content', icon: 'bi-file-text' },
        { id: 'program', label: 'Program', icon: 'bi-calendar-event' },
        { id: 'logistics', label: 'Logistics', icon: 'bi-geo-alt' },
        { id: 'media', label: 'Files, Photos & Program', icon: 'bi-file-earmark-image' },
        { id: 'admin', label: 'Registration Fee Settings', icon: 'bi-gear' }
    ];

    const baseUrl = process.env.REACT_APP_API_URL || '/api';

    // Helper function to get field value (prioritize conferenceFormData, fallback to editingConference)
    const getFieldValue = (fieldName) => {
        // Handle nested field names like "fees.standard"
        if (fieldName.includes('.')) {
            const [parent, child] = fieldName.split('.');
            const localVal = conferenceFormData[parent]?.[child];
            if (localVal !== undefined) return localVal; // preserve false / 0 / ''
            const editingVal = editingConference?.[parent]?.[child];
            return editingVal !== undefined ? editingVal : '';
        }
        if (conferenceFormData[fieldName] !== undefined) return conferenceFormData[fieldName];
        if (editingConference?.[fieldName] !== undefined) return editingConference[fieldName];
        return '';
    };

    // Helper function to get date field value (format for input[type="date"])
    const getDateFieldValue = (fieldName) => {
        const value = getFieldValue(fieldName);
        if (value) {
            return new Date(value).toISOString().split('T')[0];
        }
        return '';
    };

    // Enhanced form change handler to properly handle nested admin fields
    const handleFormChange = (e) => {
        const { type } = e.target;
        // Pass the original event so parent can access e.target.checked
        onFormChange(e);
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {editingConference ? 'Edit Conference' : 'Add New Conference'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onHide}></button>
                    </div>

                    <div className="modal-body">
                        {/* Tab Navigation */}
                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${conferenceFormTab === 'form' ? 'active' : ''}`}
                                    onClick={() => setConferenceFormTab('form')}
                                >
                                    <i className="bi bi-pencil me-1"></i>
                                    Form
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${conferenceFormTab === 'preview' ? 'active' : ''}`}
                                    onClick={() => setConferenceFormTab('preview')}
                                >
                                    <i className="bi bi-eye me-1"></i>
                                    Preview
                                </button>
                            </li>
                        </ul>

                        {/* Tab Content */}
                        {conferenceFormTab === 'form' ? (
                            <form onSubmit={onSubmit}>
                                {/* Section Navigation */}
                                <div className="mb-4">
                                    <ul className="nav nav-pills nav-fill">
                                        {formSections.map(section => (
                                            <li className="nav-item" key={section.id}>
                                                <button
                                                    type="button"
                                                    className={`nav-link ${activeFormSection === section.id ? 'active' : ''}`}
                                                    onClick={() => setActiveFormSection(section.id)}
                                                >
                                                    <i className={`${section.icon} me-2`}></i>
                                                    {section.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Basic Info Section */}
                                {activeFormSection === 'basic' && (
                                    <div>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="conference-title" className="form-label">Title <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="conference-title"
                                                    name="title"
                                                    value={getFieldValue('title')}
                                                    onChange={onFormChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="conference-location" className="form-label">Location <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="conference-location"
                                                    name="location"
                                                    value={getFieldValue('location')}
                                                    onChange={onFormChange}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="conference-startDate" className="form-label">Start Date <span className="text-danger">*</span></label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    id="conference-startDate"
                                                    name="startDate"
                                                    value={getDateFieldValue('startDate')}
                                                    onChange={onFormChange}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="conference-endDate" className="form-label">End Date <span className="text-danger">*</span></label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    id="conference-endDate"
                                                    name="endDate"
                                                    value={getDateFieldValue('endDate')}
                                                    onChange={onFormChange}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="conference-status" className="form-label">Status <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                id="conference-status"
                                                name="status"
                                                value={getFieldValue('status')}
                                                onChange={onFormChange}
                                                required
                                            >
                                                {conferenceStatuses.map(status => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>
                                            <div className="form-text mt-1 small">
                                                <strong>Active:</strong> Published & visible. <strong>Open:</strong> Broadcast conference – open for submissions. <strong>Close:</strong> Submissions closed (review/registration only). <strong>Ended:</strong> Conference has concluded.
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="conference-description" className="form-label">Description <span className="text-danger">*</span></label>
                                            <WysiwygEditor
                                                value={getFieldValue('description')}
                                                onChange={onFormChange}
                                                name="description"
                                                id="conference-description"
                                                placeholder="Enter brief description of the conference..."
                                                height="600px"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Content Section */}
                                {activeFormSection === 'content' && (
                                    <div>
                                        <div className="mb-4">
                                            <label htmlFor="conference-venue" className="form-label">Venue</label>
                                            <WysiwygEditor
                                                value={getFieldValue('venue')}
                                                onChange={onFormChange}
                                                name="venue"
                                                id="conference-venue"
                                                placeholder="Enter venue information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-socialEvents" className="form-label">Social Events</label>
                                            <WysiwygEditor
                                                value={getFieldValue('socialEvents')}
                                                onChange={onFormChange}
                                                name="socialEvents"
                                                id="conference-socialEvents"
                                                placeholder="Enter social events information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-accommodation" className="form-label">Accommodation</label>
                                            <WysiwygEditor
                                                value={getFieldValue('accommodation')}
                                                onChange={onFormChange}
                                                name="accommodation"
                                                id="conference-accommodation"
                                                placeholder="Enter accommodation information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-keynoteSpeakers" className="form-label">Keynote Speakers</label>
                                            <WysiwygEditor
                                                value={getFieldValue('keynoteSpeakers')}
                                                onChange={onFormChange}
                                                name="keynoteSpeakers"
                                                id="conference-keynoteSpeakers"
                                                placeholder="Enter keynote speakers information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-programChairs" className="form-label">Program Chairs</label>
                                            <WysiwygEditor
                                                value={getFieldValue('programChairs')}
                                                onChange={onFormChange}
                                                name="programChairs"
                                                id="conference-programChairs"
                                                placeholder="Enter program chairs information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-sponsoringInstitutions" className="form-label">Sponsoring Institutions</label>
                                            <WysiwygEditor
                                                value={getFieldValue('sponsoringInstitutions')}
                                                onChange={onFormChange}
                                                name="sponsoringInstitutions"
                                                id="conference-sponsoringInstitutions"
                                                placeholder="Enter sponsoring institutions..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-conferenceObjective" className="form-label">Conference Objective</label>
                                            <WysiwygEditor
                                                value={getFieldValue('conferenceObjective')}
                                                onChange={onFormChange}
                                                name="conferenceObjective"
                                                id="conference-conferenceObjective"
                                                placeholder="Enter conference objectives..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-areasOfInterest" className="form-label">Areas of Interest</label>
                                            <WysiwygEditor
                                                value={getFieldValue('areasOfInterest')}
                                                onChange={onFormChange}
                                                name="areasOfInterest"
                                                id="conference-areasOfInterest"
                                                placeholder="Enter areas of interest..."
                                                height="200px"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Program Section */}
                                {activeFormSection === 'program' && (
                                    <div>
                                        <div className="mb-4">
                                            <label htmlFor="conference-doctoralStudentTutorialsAndPresentations" className="form-label">Doctoral Student Research Presentations</label>
                                            <WysiwygEditor
                                                value={getFieldValue('doctoralStudentTutorialsAndPresentations')}
                                                onChange={onFormChange}
                                                name="doctoralStudentTutorialsAndPresentations"
                                                id="conference-doctoralStudentTutorialsAndPresentations"
                                                placeholder="Enter doctoral student research presentations information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-paperAndThesisSubmissionGuidelines" className="form-label">Paper and Thesis Submission Guidelines</label>
                                            <WysiwygEditor
                                                value={getFieldValue('paperAndThesisSubmissionGuidelines')}
                                                onChange={onFormChange}
                                                name="paperAndThesisSubmissionGuidelines"
                                                id="conference-paperAndThesisSubmissionGuidelines"
                                                placeholder="Enter paper and thesis submission guidelines..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-paperAwards" className="form-label">Paper Awards</label>
                                            <WysiwygEditor
                                                value={getFieldValue('paperAwards')}
                                                onChange={onFormChange}
                                                name="paperAwards"
                                                id="conference-paperAwards"
                                                placeholder="Enter paper awards information..."
                                                height="200px"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Logistics Section */}
                                {activeFormSection === 'logistics' && (
                                    <div>
                                        <div className="mb-4">
                                            <label htmlFor="conference-registrationFee" className="form-label">Registration Fee</label>
                                            <WysiwygEditor
                                                value={getFieldValue('registrationFee')}
                                                onChange={onFormChange}
                                                name="registrationFee"
                                                id="conference-registrationFee"
                                                placeholder="Enter registration fee information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-furtherInformation" className="form-label">Further Information</label>
                                            <WysiwygEditor
                                                value={getFieldValue('furtherInformation')}
                                                onChange={onFormChange}
                                                name="furtherInformation"
                                                id="conference-furtherInformation"
                                                placeholder="Enter additional information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-scientificCommittee" className="form-label">Scientific Committee</label>
                                            <WysiwygEditor
                                                value={getFieldValue('scientificCommittee')}
                                                onChange={onFormChange}
                                                name="scientificCommittee"
                                                id="conference-scientificCommittee"
                                                placeholder="Enter scientific committee information..."
                                                height="200px"
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="conference-localOrganizingCommittee" className="form-label">Local Organizing Committee</label>
                                            <WysiwygEditor
                                                value={getFieldValue('localOrganizingCommittee')}
                                                onChange={onFormChange}
                                                name="localOrganizingCommittee"
                                                id="conference-localOrganizingCommittee"
                                                placeholder="Enter local organizing committee information..."
                                                height="200px"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Admin Settings Section */}
                                {activeFormSection === 'admin' && (
                                    <div>
                                        <Alert variant="info">
                                            <i className="bi bi-info-circle me-2"></i>
                                            Admin settings for registration features.
                                        </Alert>

                                        {/* Conference Fees Section */}
                                        <Card className="mb-4">
                                            <Card.Header>
                                                <h6 className="mb-0">
                                                    <i className="bi bi-cash-coin me-2"></i>
                                                    Conference Fees
                                                </h6>
                                            </Card.Header>
                                            <Card.Body>
                                                <div className="row">
                                                    <div className="col-md-4 mb-3">
                                                        <label htmlFor="conference-fee-early-bird" className="form-label">Early Bird Fee <span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="conference-fee-early-bird"
                                                            name="fees.earlyBird"
                                                            value={getFieldValue('fees.earlyBird') || getFieldValue('fees.base') || ''}
                                                            onChange={handleFormChange}
                                                            placeholder="450.00"
                                                            step="0.01"
                                                            min="0"
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Applied through the early bird deadline.
                                                        </small>
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label htmlFor="conference-fee-regular" className="form-label">Regular Fee <span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="conference-fee-regular"
                                                            name="fees.regular"
                                                            value={getFieldValue('fees.regular') || (getFieldValue('fees.base') ? Number(getFieldValue('fees.base')) + 100 : '')}
                                                            onChange={handleFormChange}
                                                            placeholder="550.00"
                                                            step="0.01"
                                                            min="0"
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Applied after early bird until the regular deadline.
                                                        </small>
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label htmlFor="conference-fee-late" className="form-label">Late Fee <span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="conference-fee-late"
                                                            name="fees.late"
                                                            value={getFieldValue('fees.late') || (getFieldValue('fees.base') ? Math.floor((Number(getFieldValue('fees.base')) + 100) * 1.2) : '')}
                                                            onChange={handleFormChange}
                                                            placeholder="660.00"
                                                            step="0.01"
                                                            min="0"
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Applied after the regular deadline.
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-4 mb-3">
                                                        <label htmlFor="conference-fee-early" className="form-label">Early Bird Deadline <span className="text-danger">*</span></label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="conference-fee-early"
                                                            name="fees.earlyBirdDeadline"
                                                            value={getFieldValue('fees.earlyBirdDeadline')}
                                                            onChange={handleFormChange}
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Regular fee applies after this date.
                                                        </small>
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label htmlFor="conference-fee-regular-deadline" className="form-label">Regular Registration Deadline <span className="text-danger">*</span></label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="conference-fee-regular-deadline"
                                                            name="fees.regularRegistrationDeadline"
                                                            value={getFieldValue('fees.regularRegistrationDeadline')}
                                                            onChange={handleFormChange}
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Late fee applies after this date.
                                                        </small>
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label htmlFor="registration-deadline" className="form-label">Late Registration Deadline</label>
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="registration-deadline"
                                                            name="registrationDeadline"
                                                            value={getDateFieldValue('registrationDeadline')}
                                                            onChange={handleFormChange}
                                                        />
                                                        <small className="text-muted">
                                                            Final deadline for online registration.
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="conference-fee-currency" className="form-label">Currency <span className="text-danger">*</span></label>
                                                        <select
                                                            className="form-select"
                                                            id="conference-fee-currency"
                                                            name="fees.currency"
                                                            value={getFieldValue('fees.currency') || 'EUR'}
                                                            onChange={handleFormChange}
                                                            required
                                                        >
                                                            <option value="EUR">EUR (€)</option>
                                                            <option value="USD">USD ($)</option>
                                                            <option value="GBP">GBP (£)</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="conference-fee-exchange" className="form-label">EUR to USD Exchange Rate <span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="conference-fee-exchange"
                                                            name="fees.exchangeRate"
                                                            value={getFieldValue('fees.exchangeRate') || ''}
                                                            onChange={handleFormChange}
                                                            placeholder="1.15"
                                                            step="0.01"
                                                            min="0.01"
                                                            required
                                                        />
                                                        <small className="text-muted">
                                                            Users can pay in USD using this rate (e.g., 1.10 means €100 = $110)
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="conference-fee-doctoral-discount" className="form-label">Doctoral Student Discount (%)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="conference-fee-doctoral-discount"
                                                            name="fees.doctoralStudentDiscount"
                                                            value={getFieldValue('fees.doctoralStudentDiscount') ?? 25}
                                                            onChange={handleFormChange}
                                                            min="0"
                                                            max="100"
                                                            step="1"
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="conference-fee-accompanied-discount" className="form-label">Accompanied Person Discount (%)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="conference-fee-accompanied-discount"
                                                            name="fees.accompaniedPersonDiscount"
                                                            value={getFieldValue('fees.accompaniedPersonDiscount') ?? 50}
                                                            onChange={handleFormChange}
                                                            min="0"
                                                            max="100"
                                                            step="1"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Fee Structure Preview</label>
                                                        <div className="card bg-light">
                                                            <div className="card-body p-2">
                                                                <small className="text-muted">
                                                                    <strong>Early Bird:</strong> {conferenceFormData.fees?.earlyBird || conferenceFormData.fees?.base || 450} {conferenceFormData.fees?.currency || 'EUR'}<br/>
                                                                    <strong>Regular:</strong> {conferenceFormData.fees?.regular || (Number(conferenceFormData.fees?.base || 450) + 100)} {conferenceFormData.fees?.currency || 'EUR'}<br/>
                                                                    <strong>Late:</strong> {conferenceFormData.fees?.late || Math.floor(((Number(conferenceFormData.fees?.base || 450) + 100) * 1.2) / 5) * 5} {conferenceFormData.fees?.currency || 'EUR'}<br/>
                                                                    <strong>Doctoral Student ({conferenceFormData.fees?.doctoralStudentDiscount ?? 25}% off regular):</strong> {Math.floor((Number(conferenceFormData.fees?.regular || Number(conferenceFormData.fees?.base || 450) + 100) * (1 - Number(conferenceFormData.fees?.doctoralStudentDiscount ?? 25) / 100)) / 5) * 5} {conferenceFormData.fees?.currency || 'EUR'}<br/>
                                                                    <strong>Accompanied ({conferenceFormData.fees?.accompaniedPersonDiscount ?? 50}% off regular):</strong> {Math.floor((Number(conferenceFormData.fees?.regular || Number(conferenceFormData.fees?.base || 450) + 100) * (1 - Number(conferenceFormData.fees?.accompaniedPersonDiscount ?? 50) / 100)) / 5) * 5} {conferenceFormData.fees?.currency || 'EUR'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">USD Equivalent (if paid in USD)</label>
                                                        <div className="card bg-light">
                                                            <div className="card-body p-2">
                                                                <small className="text-muted">
                                                                    {(() => {
                                                                        const rate = Number(conferenceFormData.fees?.exchangeRate || 1.1);
                                                                        const earlyBird = Number(conferenceFormData.fees?.earlyBird || conferenceFormData.fees?.base || 450);
                                                                        const regular = Number(conferenceFormData.fees?.regular || Number(conferenceFormData.fees?.base || 450) + 100);
                                                                        const late = Number(conferenceFormData.fees?.late || regular * 1.2);
                                                                        return (
                                                                            <>
                                                                                <strong>Early Bird:</strong> ${Math.floor((earlyBird * rate) / 5) * 5} USD<br/>
                                                                                <strong>Regular:</strong> ${Math.floor((regular * rate) / 5) * 5} USD<br/>
                                                                                <strong>Late:</strong> ${Math.floor((late * rate) / 5) * 5} USD<br/>
                                                                                <strong>Doctoral Student ({conferenceFormData.fees?.doctoralStudentDiscount ?? 25}% off regular):</strong> ${Math.floor(((regular * (1 - Number(conferenceFormData.fees?.doctoralStudentDiscount ?? 25) / 100)) * rate) / 5) * 5} USD<br/>
                                                                                <strong>Accompanied ({conferenceFormData.fees?.accompaniedPersonDiscount ?? 50}% off regular):</strong> ${Math.floor(((regular * (1 - Number(conferenceFormData.fees?.accompaniedPersonDiscount ?? 50) / 100)) * rate) / 5) * 5} USD
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label htmlFor="conference-fee-description" className="form-label">Fee Description</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="conference-fee-description"
                                                        name="fees.description"
                                                        value={getFieldValue('fees.description')}
                                                        onChange={handleFormChange}
                                                        rows="3"
                                                        placeholder="Enter fee details, payment methods, deadlines, etc."
                                                    />
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        {/* Registration Settings */}
                                        <Card className="mb-4">
                                            <Card.Header>
                                                <h6 className="mb-0">
                                                    <i className="bi bi-person-plus me-2"></i>
                                                    Registration Settings
                                                </h6>
                                            </Card.Header>
                                            <Card.Body>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="registration-enabled"
                                                                name="registrationEnabled"
                                                                checked={!!getFieldValue('registrationEnabled')}
                                                                onChange={handleFormChange}
                                                            />
                                                            <label className="form-check-label" htmlFor="registration-enabled">
                                                                Enable Online Registration
                                                            </label>
                                                        </div>
                                                        <small className="text-muted">
                                                            Allow attendees to register online for this conference
                                                        </small>
                                                    </div>
                                                    {/* Removed 'Require Payment' option – payment is now implicitly required when fees > 0 */}
                                                </div>
                                                {/* <div className="mb-3">
                                                    <label htmlFor="max-attendees" className="form-label">Maximum Attendees</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        id="max-attendees"
                                                        name="maxAttendees"
                                                        value={getFieldValue('maxAttendees')}
                                                        onChange={onFormChange}
                                                        placeholder="Leave empty for unlimited"
                                                        min="1"
                                                    />
                                                </div> */}
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}

                                {/* Media Section */}
                                {activeFormSection === 'media' && (
                                    <div>
                                        <div className="mb-4">
                                            <label htmlFor="conference-programHtml" className="form-label">Conference Program (HTML)</label>
                                            <WysiwygEditor
                                                value={getFieldValue('programHtml')}
                                                onChange={onFormChange}
                                                name="programHtml"
                                                id="conference-programHtml"
                                                placeholder="Enter the conference program in HTML format..."
                                                height="260px"
                                            />
                                        </div>

                                        <div className="border-top border border-5 my-4"></div>

                                        {/* Display existing files */}
                                        {editingConference && editingConference.files && editingConference.files.length > 0 && (
                                            <Card className="mb-3">
                                                <Card.Header>
                                                    <h6 className="mb-0">
                                                        <i className="bi bi-files me-2"></i>
                                                        Existing Files
                                                    </h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="row">
                                                        {editingConference.files.map((file, index) => (
                                                            <div key={index} className="col-md-12 mb-2">
                                                                <div className="d-flex justify-content-between align-items-center border p-3 rounded">
                                                                    <div className="d-flex align-items-center flex-grow-1">
                                                                        <i className={`${getFileIcon(file.mimetype)} me-2`}></i>
                                                                        <div className="flex-grow-1">
                                                                            <span className="fw-medium" title={file.originalName}>
                                                                                {file.originalName}
                                                                            </span>
                                                                            <br />
                                                                            <small className="text-muted">
                                                                                {(file.size / 1024).toFixed(1)} KB
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                    <div className="btn-group btn-group-sm align-items-center">
                                                                        <div className="form-check m-0 me-2 d-flex align-items-center">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                id={`booklet-${file.filename}`}
                                                                                checked={!!file.isBooklet}
                                                                                onChange={() => onSetBookletFile && onSetBookletFile(editingConference._id, file.filename)}
                                                                                title="Mark as booklet"
                                                                            />
                                                                            <label className="form-check-label small ms-1" htmlFor={`booklet-${file.filename}`}>
                                                                                Booklet
                                                                            </label>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-primary"
                                                                            onClick={() => {
                                                                                const url = `${baseUrl}/conferences/files/${file.filename}`;
                                                                                window.open(url, '_blank');
                                                                            }}
                                                                            title="Download file"
                                                                        >
                                                                            <i className="bi bi-download"></i>
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-secondary"
                                                                            onClick={() => {
                                                                                const url = `${baseUrl}/conferences/files/${file.filename}`;
                                                                                navigator.clipboard.writeText(url).then(() => {
                                                                                    alert('File URL copied to clipboard!');
                                                                                }).catch(() => {
                                                                                    alert('Failed to copy URL');
                                                                                });
                                                                            }}
                                                                            title="Copy file URL"
                                                                        >
                                                                            <i className="bi bi-link-45deg"></i>
                                                                        </button>
                                                                        {onDeleteFile && (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-danger"
                                                                                onClick={() => onDeleteFile(editingConference._id, file.filename)}
                                                                                title="Delete file"
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        )}

                                        <div className="row">
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="conference-files" className="form-label">Upload Files (Max 10 Files)</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    id="conference-files"
                                                    name="files"
                                                    multiple
                                                    onChange={onFormChange}
                                                    accept=".pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx"
                                                />
                                                <div className="form-text">
                                                    Accepted formats: PDF, DOC, DOCX, TXT, PPT, PPTX, XLS, XLSX
                                                </div>
                                            </div>
                                        </div>

                                        {/* Separator between Files and Photos */}
                                        <div className="border-top border border-5 my-4"></div>

                                        {/* Display existing photos */}
                                        {editingConference && editingConference.photos && editingConference.photos.length > 0 && (
                                            <Card className="mb-3">
                                                <Card.Header>
                                                    <h6 className="mb-0">
                                                        <i className="bi bi-images me-2"></i>
                                                        Existing Photos
                                                    </h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="row">
                                                        {editingConference.photos.map((photo, index) => (
                                                            <div key={index} className="col-md-3 mb-3">
                                                                <div className="position-relative">
                                                                    <img
                                                                        src={`${baseUrl}/conferences/photos/${photo.filename}`}
                                                                        alt={photo.originalName}
                                                                        className="img-fluid rounded"
                                                                        style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                                                                    />
                                                                    <div className="position-absolute top-0 end-0 m-1">
                                                                        <div className="btn-group-vertical btn-group-sm">
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-primary btn-sm"
                                                                                onClick={() => {
                                                                                    const url = `${baseUrl}/conferences/photos/${photo.filename}`;
                                                                                    window.open(url, '_blank');
                                                                                }}
                                                                                title="Download photo"
                                                                            >
                                                                                <i className="bi bi-download"></i>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-secondary btn-sm"
                                                                                onClick={() => {
                                                                                    const url = `${baseUrl}/conferences/photos/${photo.filename}`;
                                                                                    navigator.clipboard.writeText(url).then(() => {
                                                                                        alert('Photo URL copied to clipboard!');
                                                                                    }).catch(() => {
                                                                                        alert('Failed to copy URL');
                                                                                    });
                                                                                }}
                                                                                title="Copy photo URL"
                                                                            >
                                                                                <i className="bi bi-link-45deg"></i>
                                                                            </button>
                                                                            {onDeletePhoto && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-danger btn-sm"
                                                                                    onClick={() => onDeletePhoto(editingConference._id, photo.filename)}
                                                                                    title="Delete photo"
                                                                                >
                                                                                    <i className="bi bi-trash"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <small className="text-muted d-block mt-1 text-truncate">
                                                                    {photo.originalName}
                                                                </small>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        )}

                                        <div className="row">
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="conference-photos" className="form-label">Upload Photos (Max 30 photos at a time)</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    id="conference-photos"
                                                    name="photos"
                                                    multiple
                                                    onChange={onFormChange}
                                                    accept="image/*"
                                                />
                                                <div className="form-text">
                                                    Accepted formats: JPG, JPEG, PNG, GIF, WEBP
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                )}
                            </form>
                        ) : (
                            /* Preview Tab */
                            <div className="wysiwyg-preview">
                                <h4>Conference Preview</h4>
                                
                                {/* Basic Information Preview */}
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Title:</strong> {conferenceFormData.title || 'No title'}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Location:</strong> {conferenceFormData.location || 'No location'}
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <strong>Start Date:</strong> {conferenceFormData.startDate || 'No date'}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>End Date:</strong> {conferenceFormData.endDate || 'No date'}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <strong>Status:</strong> 
                                    <span className="badge bg-primary ms-2">{conferenceFormData.status}</span>
                                </div>

                                {/* Content Fields Preview */}
                                {conferenceFormData.description && (
                                    <div className="mb-3">
                                        <strong>Description:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.description }} />
                                    </div>
                                )}

                                {conferenceFormData.venue && (
                                    <div className="mb-3">
                                        <strong>Venue:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.venue }} />
                                    </div>
                                )}

                                {conferenceFormData.socialEvents && (
                                    <div className="mb-3">
                                        <strong>Social Events:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.socialEvents }} />
                                    </div>
                                )}

                                {conferenceFormData.accommodation && (
                                    <div className="mb-3">
                                        <strong>Accommodation:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.accommodation }} />
                                    </div>
                                )}

                                {conferenceFormData.keynoteSpeakers && (
                                    <div className="mb-3">
                                        <strong>Keynote Speakers:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.keynoteSpeakers }} />
                                    </div>
                                )}

                                {conferenceFormData.programChairs && (
                                    <div className="mb-3">
                                        <strong>Program Chairs:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.programChairs }} />
                                    </div>
                                )}

                                {conferenceFormData.programHtml && (
                                    <div className="mb-3">
                                        <strong>Conference Program:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.programHtml }} />
                                    </div>
                                )}

                                {conferenceFormData.sponsoringInstitutions && (
                                    <div className="mb-3">
                                        <strong>Sponsoring Institutions:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.sponsoringInstitutions }} />
                                    </div>
                                )}

                                {conferenceFormData.conferenceObjective && (
                                    <div className="mb-3">
                                        <strong>Conference Objective:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.conferenceObjective }} />
                                    </div>
                                )}

                                {conferenceFormData.areasOfInterest && (
                                    <div className="mb-3">
                                        <strong>Areas of Interest:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.areasOfInterest }} />
                                    </div>
                                )}

                                {conferenceFormData.doctoralStudentTutorialsAndPresentations && (
                                    <div className="mb-3">
                                        <strong>Doctoral Student Research Presentations:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.doctoralStudentTutorialsAndPresentations }} />
                                    </div>
                                )}

                                {conferenceFormData.paperAndThesisSubmissionGuidelines && (
                                    <div className="mb-3">
                                        <strong>Paper and Thesis Submission Guidelines:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.paperAndThesisSubmissionGuidelines }} />
                                    </div>
                                )}

                                {conferenceFormData.paperAwards && (
                                    <div className="mb-3">
                                        <strong>Paper Awards:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.paperAwards }} />
                                    </div>
                                )}

                                {conferenceFormData.registrationFee && (
                                    <div className="mb-3">
                                        <strong>Registration Fee:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.registrationFee }} />
                                    </div>
                                )}

                                {conferenceFormData.furtherInformation && (
                                    <div className="mb-3">
                                        <strong>Further Information:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.furtherInformation }} />
                                    </div>
                                )}

                                {conferenceFormData.scientificCommittee && (
                                    <div className="mb-3">
                                        <strong>Scientific Committee:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.scientificCommittee }} />
                                    </div>
                                )}

                                {conferenceFormData.localOrganizingCommittee && (
                                    <div className="mb-3">
                                        <strong>Local Organizing Committee:</strong>
                                        <div className="border p-2 mt-1" dangerouslySetInnerHTML={{ __html: conferenceFormData.localOrganizingCommittee }} />
                                    </div>
                                )}

                                {/* Fees Preview */}
                                {conferenceFormData.fees && (
                                    conferenceFormData.fees.base ||
                                    conferenceFormData.fees.earlyBird ||
                                    conferenceFormData.fees.regular ||
                                    conferenceFormData.fees.late ||
                                    conferenceFormData.fees.earlyBirdDeadline ||
                                    conferenceFormData.fees.regularRegistrationDeadline ||
                                    conferenceFormData.fees.exchangeRate ||
                                    conferenceFormData.fees.description
                                ) && (
                                    <div className="mb-3">
                                        <strong>Conference Fees:</strong>
                                        <div className="border p-2 mt-1">
                                            {conferenceFormData.fees?.base && (
                                                <p className="mb-1">
                                                    <strong>Base:</strong> {conferenceFormData.fees.base} {conferenceFormData.fees?.currency || 'EUR'}
                                                </p>
                                            )}
                                            {conferenceFormData.fees?.earlyBirdDeadline && (
                                                <p className="mb-1">
                                                    <strong>Early Bird Deadline:</strong> {conferenceFormData.fees.earlyBirdDeadline}
                                                </p>
                                            )}
                                            {conferenceFormData.fees?.regularRegistrationDeadline && (
                                                <p className="mb-1">
                                                    <strong>Regular Deadline:</strong> {conferenceFormData.fees.regularRegistrationDeadline}
                                                </p>
                                            )}
                                            {conferenceFormData.fees?.exchangeRate && (
                                                <p className="mb-1">
                                                    <strong>EUR to USD Exchange Rate:</strong> {conferenceFormData.fees.exchangeRate}
                                                </p>
                                            )}
                                            {conferenceFormData.fees?.description && (
                                                <p className="mb-0">
                                                    <strong>Description:</strong> {conferenceFormData.fees.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Header Photo Preview */}
                                {editingConference && editingConference.headerPhoto && (
                                    <div className="mb-3">
                                        <strong>Header Photo:</strong>
                                        <div className="border p-2 mt-1">
                                            <img
                                                src={`${baseUrl}/conferences/photos/${editingConference.headerPhoto}`}
                                                alt="Header photo"
                                                className="img-fluid rounded"
                                                style={{ maxWidth: '300px', height: 'auto' }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" onClick={onSubmit}>
                            {editingConference ? 'Update Conference' : 'Create Conference'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConferenceModal;
